import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import SalesManage, { type SalesManageSaveReason } from '../SalesManage';
import type { Invoice } from '../../types/invoice';
import { useSalesTransactionsHub } from './SalesTransactionsHubContext';

const SalesInvoiceOverlay = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useSalesTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');

  const close = useCallback(() => {
    navigate({ pathname: '/sales-transactions', search: listSearch }, { preventScrollReset: true });
  }, [listSearch, navigate]);

  const isNew = id === 'new';
  const parsedId = id != null ? Number(id) : Number.NaN;
  const invoiceId = isNew || Number.isNaN(parsedId) || parsedId <= 0 ? undefined : parsedId;

  const handleInvoiceSaved = useCallback(
    (invoice: Invoice, reason: SalesManageSaveReason) => {
      refetchList();

      if (reason === 'draft_created') {
        navigate(
          { pathname: `/sales-transactions/invoice/${invoice.id}`, search: listSearch },
          { replace: true, preventScrollReset: true }
        );
        return;
      }

      if (reason === 'posted' || reason === 'amended') {
        navigate(
          {
            pathname: `/sales-transactions/view/invoice/${invoice.id}`,
            search: listSearch,
          },
          { replace: true, preventScrollReset: true }
        );
      }
    },
    [close, listSearch, navigate, refetchList]
  );

  return (
    <TransactionOverlay headerless variant="wide" onClose={close}>
      <SalesManage
        key={invoiceId ?? 'new'}
        embedded
        invoiceId={invoiceId}
        onClose={close}
        onInvoiceSaved={handleInvoiceSaved}
      />
    </TransactionOverlay>
  );
};

export default SalesInvoiceOverlay;
